/**
 * Handler manager for dynamic handler management
 */

import type { RequestHandler } from 'msw'
import type { HandlerManager } from './types'
import { getMswInstance } from '../core/setup'

/**
 * Implementation of handler manager
 */
export class MockHandlerManager implements HandlerManager {
  private handlers = new Set<RequestHandler>()
  private enabled = true

  /**
   * Add handlers
   */
  add(...handlers: RequestHandler[]): void {
    for (const handler of handlers) {
      this.handlers.add(handler)
    }

    if (this.enabled) {
      this.applyHandlers()
    }
  }

  /**
   * Remove handlers
   */
  remove(...handlers: RequestHandler[]): void {
    for (const handler of handlers) {
      this.handlers.delete(handler)
    }

    if (this.enabled) {
      this.applyHandlers()
    }
  }

  /**
   * Replace all handlers
   */
  replace(handlers: RequestHandler[]): void {
    this.handlers.clear()
    for (const handler of handlers) {
      this.handlers.add(handler)
    }

    if (this.enabled) {
      this.applyHandlers()
    }
  }

  /**
   * Get current handlers
   */
  getHandlers(): RequestHandler[] {
    return Array.from(this.handlers)
  }

  /**
   * Enable/disable handlers
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled
    this.applyHandlers()
  }

  /**
   * Check if enabled
   */
  isEnabled(): boolean {
    return this.enabled
  }

  /**
   * Apply handlers to MSW instance
   */
  private applyHandlers(): void {
    const instance = getMswInstance()
    if (!instance) {
      console.warn('MSW instance not available, cannot apply handlers')
      return
    }

    if (this.enabled) {
      instance.resetHandlers(...this.getHandlers())
    } else {
      instance.resetHandlers()
    }
  }

  /**
   * Clear all handlers
   */
  clear(): void {
    this.handlers.clear()
    if (this.enabled) {
      this.applyHandlers()
    }
  }

  /**
   * Get handler count
   */
  getHandlerCount(): number {
    return this.handlers.size
  }

  /**
   * Check if handler exists
   */
  hasHandler(handler: RequestHandler): boolean {
    return this.handlers.has(handler)
  }
}

/**
 * Global handler manager instance
 */
export const globalHandlerManager = new MockHandlerManager()

/**
 * Add handlers globally
 */
export function addHandlers(...handlers: RequestHandler[]): void {
  globalHandlerManager.add(...handlers)
}

/**
 * Remove handlers globally
 */
export function removeHandlers(...handlers: RequestHandler[]): void {
  globalHandlerManager.remove(...handlers)
}

/**
 * Replace all handlers globally
 */
export function replaceHandlers(handlers: RequestHandler[]): void {
  globalHandlerManager.replace(handlers)
}

/**
 * Get current handlers
 */
export function getCurrentHandlers(): RequestHandler[] {
  return globalHandlerManager.getHandlers()
}

/**
 * Enable/disable handler management
 */
export function setHandlersEnabled(enabled: boolean): void {
  globalHandlerManager.setEnabled(enabled)
}

/**
 * Check if handlers are enabled
 */
export function areHandlersEnabled(): boolean {
  return globalHandlerManager.isEnabled()
}

/**
 * Clear all handlers
 */
export function clearHandlers(): void {
  globalHandlerManager.clear()
}

/**
 * Create a scoped handler manager
 */
export function createHandlerManager(): MockHandlerManager {
  return new MockHandlerManager()
}
